import React from 'react';
import { ArrowRight, Calendar, ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react';
import { ProjectLockHint } from '../ProjectLockHint';
import type { ProjectLock } from '../../services/projectLockLogic';
import { DashboardProject, substitutionProgress } from './dashboardModel';
import { IconButton, Panel, PanelHeader, Tag } from './dashboardUi';

interface Props {
    subs: ReadonlyArray<DashboardProject>;
    myEmail: string;
    userId: string | null | undefined;
    now: Date;
    locks: ReadonlyMap<string, ProjectLock>;
    lockNowMs: number;
    collapsed: boolean;
    onToggle: () => void;
    onOpen: (project: DashboardProject) => void;
    /** Sichtbare Projekte — eine Tochter findet hier ihre Mutter (dort stehen ihre Buchungen). */
    projectsById?: ReadonlyMap<string, DashboardProject>;
}

/**
 * Urlaubsvertretungen: je Projekt Soll/Ist der Begehungen im Zeitraum
 * (Soll aus den Turnusterminen, erledigt = jede Begehung im Zeitraum, auch
 * die eines Kollegen). Zustandsfarbe traegt hier Bedeutung: rot = noch
 * nichts, bernstein = im Rueckstand, gruen = im Soll.
 */
export const SubstitutionPanel: React.FC<Props> = ({ subs, myEmail, userId, now, locks, lockNowMs, collapsed, onToggle, onOpen, projectsById }) => {
    if (subs.length === 0) return null;
    const parentOf = (p: DashboardProject) =>
        typeof p.parent_project_id === 'string' ? projectsById?.get(p.parent_project_id) : undefined;
    const stats = subs.map(p => ({ p, s: substitutionProgress(p, myEmail, userId, now, parentOf(p)) }));
    const done = stats.reduce((a, x) => a + x.s.completed, 0);
    const target = stats.reduce((a, x) => a + x.s.target, 0);
    return (
        <Panel>
            <PanelHeader
                icon={<ShieldAlert />}
                title={`${subs.length} Vertretung${subs.length > 1 ? 'en' : ''}`}
                sub={target > 0 ? `${done}/${target} Begehungen erledigt` : undefined}
                actions={<IconButton label={collapsed ? 'Aufklappen' : 'Zuklappen'} onClick={onToggle} aria-expanded={!collapsed}>{collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}</IconButton>}
            />
            {!collapsed && (
                <div className="p-3 grid grid-cols-1 gap-2.5">
                    {stats.map(({ p, s }) => {
                        const meta = p.data.metadata;
                        const barColor = s.completed === 0 ? 'bg-red-500' : s.completed < s.target ? 'bg-amber-500' : 'bg-emerald-500';
                        const badgeTone = s.completed === 0 ? 'red' : s.completed < s.target ? 'amber' : 'emerald';
                        return (
                            <button type="button" key={p.id} onClick={() => onOpen(p)} className="text-left p-3 rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50/30 transition-colors group min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="min-w-0 flex-1">
                                        {meta?.projectNumber && <span className="text-[11px] font-mono text-slate-500">{meta.projectNumber}</span>}
                                        <h4 className="text-xs font-semibold text-slate-800 truncate group-hover:text-teal-700">{p.name}</h4>
                                        <div className="text-[11px] text-slate-500 mt-0.5">Für: <span className="font-medium text-slate-600">{s.coordinator || '—'}</span></div>
                                        <ProjectLockHint lock={locks.get(p.id)} currentUserId={userId} nowMs={lockNowMs} className="mt-1" />
                                    </div>
                                    <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-teal-600 shrink-0 mt-1" />
                                </div>
                                {s.isHoliday && s.subStart && s.subEnd && (
                                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                        <Tag tone="teal"><Calendar className="w-3 h-3" />{s.subStart.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} – {s.subEnd.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}</Tag>
                                        <Tag>{s.durationDays} {s.durationDays === 1 ? 'Tag' : 'Tage'}</Tag>
                                        {s.daysLeft !== null && s.daysLeft > 0 && <Tag tone="blue">noch {s.daysLeft} T.</Tag>}
                                        {s.daysLeft === 0 && <Tag tone="amber">Letzter Tag</Tag>}
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    <Tag tone={badgeTone}>{s.target > 0 ? `${s.completed}/${s.target} erledigt` : `${s.completed} erledigt`}</Tag>
                                    {s.target > 0 && (
                                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                            <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${s.pct}%` }} />
                                        </div>
                                    )}
                                    {s.target > 0 && <span className="text-[11px] font-semibold text-slate-500 tabular">{s.pct}%</span>}
                                </div>
                                {s.visitedByOthers.length > 0 && (
                                    <div className="text-[11px] text-amber-700 mt-1.5">begangen von {s.visitedByOthers.join(', ')}</div>
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </Panel>
    );
};
